'use client';

import React, { createContext, useContext } from 'react';

import { useGlobalState } from '@/components/global/global-state';
import { constructTeamPageUrl } from '@/helpers/organization';
import { TeamViews } from '@/lib/constants/team';
import { useNextCookieState } from '@/lib/next-cookie-state';

export interface CurrentOrganizationInterface {
    canSelectCurrentOrganization: boolean;
    currentOrganizationId: string;
    currentOrganizationAnalysisResultsUrl: string;
    currentOrganizationCompanyEvaluationsUrl: string;
    currentOrganizationIndicesUrl: string;
    currentOrganizationMembersUrl: string;
    currentOrganizationProfileUrl: string;
    currentOrganizationProjectsUrl: string;
    currentOrganizationResearchUrl: string;
    currentOrganizationDashboardUrl: string;
    setCurrentOrganizationId: ReactStateSetter<string>;
}

const CurrentOrganizationContext = createContext({} as CurrentOrganizationInterface);

const CurrentOrganizationProvider = ({
    children,
    currentSelectedOrganizationId = '',
}: {
    children: React.ReactNode;
    currentSelectedOrganizationId?: string;
}) => {
    const { globalState } = useGlobalState();
    const { currentUser } = globalState;

    const [currentOrganizationId, setCurrentOrganizationId] = useNextCookieState(
        'currentOrganization',
        currentUser?.primaryOrganization?.id ?? currentSelectedOrganizationId
    );
    const canSelectCurrentOrganization = !!currentUser?.isThematicAdmin;
    const currentOrganizationIndicesUrl =
        currentOrganizationId &&
        constructTeamPageUrl({ organizationId: currentOrganizationId, teamView: TeamViews.INDICES });
    const currentOrganizationMembersUrl =
        currentOrganizationId &&
        constructTeamPageUrl({ organizationId: currentOrganizationId, teamView: TeamViews.MEMBERS });
    const currentOrganizationProfileUrl =
        currentOrganizationId &&
        constructTeamPageUrl({ organizationId: currentOrganizationId, teamView: TeamViews.PROFILE });
    const currentOrganizationResearchUrl =
        currentOrganizationId &&
        constructTeamPageUrl({ organizationId: currentOrganizationId, teamView: TeamViews.RESEARCH });
    const currentOrganizationDashboardUrl =
        currentOrganizationId &&
        constructTeamPageUrl({ organizationId: currentOrganizationId, teamView: TeamViews.DASHBOARD });
    const currentOrganizationProjectsUrl =
        currentOrganizationId &&
        constructTeamPageUrl({ organizationId: currentOrganizationId, teamView: TeamViews.PROJECTS });
    const currentOrganizationAnalysisResultsUrl =
        currentOrganizationId &&
        constructTeamPageUrl({ organizationId: currentOrganizationId, teamView: TeamViews.ANALYSIS_RESULTS });
    const currentOrganizationCompanyEvaluationsUrl =
        currentOrganizationId &&
        constructTeamPageUrl({ organizationId: currentOrganizationId, teamView: TeamViews.COMPANY_EVALUATIONS });

    return (
        <CurrentOrganizationContext.Provider
            value={{
                canSelectCurrentOrganization,
                currentOrganizationAnalysisResultsUrl,
                currentOrganizationCompanyEvaluationsUrl,
                currentOrganizationDashboardUrl,
                currentOrganizationId,
                currentOrganizationIndicesUrl,
                currentOrganizationMembersUrl,
                currentOrganizationProfileUrl,
                currentOrganizationProjectsUrl,
                currentOrganizationResearchUrl,
                setCurrentOrganizationId,
            }}
        >
            {children}
        </CurrentOrganizationContext.Provider>
    );
};

const useCurrentOrganization = () => {
    const context = useContext(CurrentOrganizationContext);
    if (!context) {
        throw new Error('useCurrentOrganization must be used within a CurrentOrganizationContext');
    }
    return context;
};

export { CurrentOrganizationProvider, useCurrentOrganization };
